import React, { useEffect, useRef, useState } from "react";

// Count-up runs once when the proof bar enters the viewport. Smooth ease-out
// via rAF, no third-party dep. Restarts only if the section fully leaves and
// re-enters (a genuine re-scroll into view), not on tiny viewport wiggles.
const useCountUpOnce = (from, to, durationMs = 1800) => {
  const [value, setValue] = useState(from);
  const ref = useRef(null);
  const rafRef = useRef(0);
  const playedRef = useRef(false);
  const outOfViewRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setValue(to);
      return undefined;
    }
    const run = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(from + (to - from) * eased));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            outOfViewRef.current = false;
            run();
          } else if (!entry.isIntersecting) {
            outOfViewRef.current = true;
          } else if (entry.isIntersecting && outOfViewRef.current) {
            // came back into view after leaving — replay
            outOfViewRef.current = false;
            cancelAnimationFrame(rafRef.current);
            setValue(from);
            run();
          }
        });
      },
      { threshold: 0.55 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [from, to, durationMs]);

  return [value, ref];
};

// A quiet band between the hero and Selected Work — a single company
// statement followed by one production metric with a subtle count-up.
export const IntroBar = () => {
  const [count, proofRef] = useCountUpOnce(995, 1000, 1800);
  return (
    <section
      id="intro"
      data-testid="intro-bar"
      className="relative w-full pt-24 md:pt-36 pb-16 md:pb-20"
      style={{ background: "rgba(8, 8, 8, 0.88)" }}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <p
          className="leading-[1.45] mx-auto"
          style={{
            fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
            letterSpacing: "-0.01em",
            color: "#F5F5F3",
            opacity: 0.88,
            fontWeight: 400,
            maxWidth: "760px",
          }}
          data-testid="intro-statement"
        >
          Poland-based production studio creating scripted series,
          international formats and mobile-first entertainment for audiences
          worldwide.
        </p>

        <div
          ref={proofRef}
          className="mt-16 md:mt-24 flex flex-col items-center"
          data-testid="proof-bar"
        >
          <div
            className="leading-none tabular-nums"
            style={{
              fontSize: "clamp(3.5rem, 9.5vw, 8rem)",
              letterSpacing: "-0.045em",
              fontWeight: 500,
              color: "#F5F5F3",
              fontVariantNumeric: "tabular-nums",
            }}
            data-testid="proof-figure"
          >
            {count.toLocaleString("en-US")}
            <span style={{ color: "#FF6A3D" }}>+</span>
          </div>
          <div
            className="mt-5 md:mt-6"
            style={{
              fontSize: "clamp(0.72rem, 0.85vw, 0.85rem)",
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "#A1A1AA",
              fontWeight: 500,
            }}
            data-testid="proof-label"
          >
            Minutes of High-Quality Storytelling Produced
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroBar;
